type RgbColor = { r: number; g: number; b: number };

const clampChannel = (value: number) => Math.min(255, Math.max(0, value));

const parseHexColor = (input: string): RgbColor | null => {
  const hex = input.replace("#", "").trim();
  if (!/^[0-9a-f]+$/i.test(hex)) return null;

  if (hex.length === 3) {
    const r = Number.parseInt(hex[0] + hex[0], 16);
    const g = Number.parseInt(hex[1] + hex[1], 16);
    const b = Number.parseInt(hex[2] + hex[2], 16);
    return { r, g, b };
  }

  if (hex.length === 6 || hex.length === 8) {
    const r = Number.parseInt(hex.slice(0, 2), 16);
    const g = Number.parseInt(hex.slice(2, 4), 16);
    const b = Number.parseInt(hex.slice(4, 6), 16);
    return { r, g, b };
  }

  return null;
};

const parseRgbComponent = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed.endsWith("%")) {
    const percent = Number.parseFloat(trimmed.slice(0, -1));
    if (!Number.isFinite(percent)) return null;
    return clampChannel((percent / 100) * 255);
  }

  const numeric = Number.parseFloat(trimmed);
  if (!Number.isFinite(numeric)) return null;
  return clampChannel(numeric);
};

const parseRgbColor = (input: string): RgbColor | null => {
  const match = input.match(/rgba?\(([^)]+)\)/i);
  if (!match) return null;
  const parts = match[1].split(",").map((part) => parseRgbComponent(part));
  if (parts.length < 3) return null;
  const [r, g, b] = parts;
  if (r == null || g == null || b == null) return null;
  return { r, g, b };
};

const parseColor = (input: string): RgbColor | null => {
  if (!input) return null;
  const trimmed = input.trim();
  if (trimmed.startsWith("#")) {
    return parseHexColor(trimmed);
  }
  if (trimmed.toLowerCase().startsWith("rgb")) {
    return parseRgbColor(trimmed);
  }
  return null;
};

const relativeLuminance = ({ r, g, b }: RgbColor) => {
  const toLinear = (value: number) => {
    const channel = value / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

export const isLightColor = (input: string) => {
  const rgb = parseColor(input);
  if (!rgb) return false;
  return relativeLuminance(rgb) > 0.6;
};
